import type {
  ActionItem,
  ActionItemEventType,
  ActionItemSuggestedAction,
} from '~/data/schemas/actionItem';
import type { LocaleTerms } from '~/hooks/useLocaleTerms';
import { formatCompactCurrency } from '~/utils/format';

export const EM_DASH = '—';

export const getEventTypeLabels = (terms: LocaleTerms): Record<ActionItemEventType, string> => ({
  'invoice.needs_review': 'Invoice review',
  'proposal.needs_review': 'Proposal review',
  'dispute.needs_attention': 'Dispute attention',
  'dispute.vendor_reply_overdue': 'Vendor reply overdue',
  'dispute.delivery_failed': 'Dispute email bounced',
  'certificate.expiring': 'Certificate expiring',
  'certificate.expired': 'Certificate expired',
  'violation.overdue': `${terms.violation} overdue`,
  'contract.expiring': 'Contract expiring',
  'contract.expired': 'Contract expired',
  'split.needs_review': 'Split review',
  'document.needs_review': 'Document review',
  'task.follow_up_due': 'Follow-up due',
  'task.follow_up_overdue': 'Follow-up overdue',
});

// The non-empty suggested actions, i.e. the ones the AI actually recommends.
export type Suggestion = Exclude<ActionItemSuggestedAction, ''>;

export const SUGGESTED_ACTION_LABEL: Record<Suggestion, string> = {
  reject: 'Reject',
  partial: 'Partial approval',
  approve: 'Approve',
};

// MUI Chip colors for each suggestion — reject reads as the urgent one.
export const SUGGESTED_ACTION_COLOR: Record<Suggestion, 'error' | 'warning' | 'success'> = {
  reject: 'error',
  partial: 'warning',
  approve: 'success',
};

export const SUGGESTION_OPTIONS: Suggestion[] = ['reject', 'partial', 'approve'];

/**
 * Derive a short category noun from an event type by capitalizing the prefix
 * before the first dot. e.g. `invoice.needs_review` -> `Invoice`.
 */
export function eventTypeCategory(eventType: string, terms: LocaleTerms): string {
  const prefix = eventType.split('.')[0] ?? '';
  if (!prefix) return '';
  // UK accounts call violations defects, as the compliance screens already do.
  if (prefix === 'violation') return terms.violation;
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

/**
 * Compact relative age from a created-at date. Accepts an optional `now`
 * reference for deterministic testing.
 *   - same day            -> `today`
 *   - 1–6 days            -> `Nd`
 *   - 7–29 days           -> `Nw`
 *   - 30+ days            -> `Nmo`
 * Future dates (negative elapsed days) also return `today`.
 */
export function formatAge(createdAt: string, now: Date = new Date()): string {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return EM_DASH;

  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.floor((now.getTime() - created.getTime()) / dayMs);

  if (days <= 0) return 'today';
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

/**
 * Render an action item's value-at-stake as compact currency, or an em dash
 * when there's no monetary value (e.g. compliance items). `value` is the
 * backend's decimal string.
 */
export function formatValueAtStake(value: string | null, currencyCode: string): string {
  if (!value) return EM_DASH;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return EM_DASH;
  return formatCompactCurrency(n, currencyCode);
}

/**
 * The title to show for an item. Reviewed invoices arrive titled with the AI's
 * verdict + reason (e.g. "Invoice rejected — service billed at overtime"). When
 * the contract analysis chip already conveys the verdict, drop that leading clause
 * so the title is just the reason. Items with no suggestion keep their title.
 */
export function displayTitle(
  item: Pick<ActionItem, 'title' | 'suggestedAction' | 'eventType'>,
  terms: LocaleTerms,
): string {
  const title = localizedTitle(item, terms);
  if (!item.suggestedAction) return title;
  const idx = title.indexOf(EM_DASH);
  if (idx === -1) return title;
  const reason = title.slice(idx + EM_DASH.length).trim();
  if (!reason) return title;
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}

/**
 * Titles arrive from the API locale-agnostic, and an overdue-violation one always
 * opens with "Violation" — the noun UK accounts read as "Defect".
 */
function localizedTitle(item: Pick<ActionItem, 'title' | 'eventType'>, terms: LocaleTerms): string {
  if (item.eventType !== 'violation.overdue') return item.title;
  return item.title.replace(/^Violation\b/, terms.violation);
}
